import { Injectable } from "@angular/core";
import { ReplaySubject, Subject } from "rxjs";
/***********************/
/* BFX */
// multi-asset
import * as CubiqTS from "shared/cubiq-model-multi-asset";
import {
  Rate,
  RateSubscription,
  RatePromiseService,
  RateCache,
  CURVE_SPT, // CURVE_FWD, CURVE_NDF,
  MarketDataEventTransformer,
  RateSubscriptionPromiseService,
  CcyPairPromiseLookupService,
  CcyPairLookupService,
} from "shared/cubiq-services";
//
import { OENEntryType } from "../../data/enums/oe-entry-type.enum";
//
import { OERateDataModel } from "../../data/models/rate/oe-rate-data.model";
import { OERateDisplayModel } from "../../data/models/rate/oe-rate-display.model";
import { OERateTransportModel } from "../../data/models/rate/oe-rate-transport.model";
import { OEIncomingRateSubscriptionModel } from "../../data/models/rate/oe-rate-subscription-incoming.model";
import { OEOutgoingRateSubscriptionModel } from "../../data/models/rate/oe-rate-subscription-outgoing.model";
import { OENRateSubscriptionState } from "../../data/enums/oe-rate-subscription-state.enum";
import { OELoggerService } from "./oe-logger.service";
import { OEUtilityService } from "./oe-utility.service";
import { LoggerService } from "@bfx/logger";
const logger = LoggerService.for(__dirname);

@Injectable()
export class OERateSubscriptionManagerService {
  /**********************************************************************/
  /* DATA */
  protected incomingRateSubscriptions: {
    [RateSubscriptionKey: string]: OEIncomingRateSubscriptionModel;
  } = {};
  protected outgoingRateSubscriptions: {
    [RateSubscriptionKey: string]: OEOutgoingRateSubscriptionModel;
  } = {};

  private deleteTimeoutScheduled: any = null;

  /**********************************************************************/
  /* CONSTRUCTOR*/
  constructor(
    protected rateService: RatePromiseService,
    protected rateCache: RateCache,
    protected rateTransformer: MarketDataEventTransformer,
    protected rateSubscriptionService: RateSubscriptionPromiseService,
    protected currencyPairService: CcyPairPromiseLookupService,
    protected oeLogger: OELoggerService,
    private utility: OEUtilityService,
  ) {
    this.oeLogger = oeLogger.for(__dirname);
    this.initialize();
  }

  /**********************************************************************/
  /* INITIALIZATION */

  public initialize(): void {
    this.listenToIncomingRates();
  }

  /**********************************************************************/
  /* INCOMING RATE SUBSCRIPTION */

  private listenToIncomingRates(): void {
    this.rateService.getRateEventSubject().subscribe((rates: Array<Rate>) => {
      let activeRates: Array<Rate> = rates.filter((item: Rate) =>
        Object.keys(this.incomingRateSubscriptions).includes(item.key),
      );
      this.processIncomingRates(activeRates);
    });
  }

  private processIncomingRates(activeRates: Array<Rate>): void {
    if (activeRates instanceof Array && activeRates.length > 0) {
      for (let activeRate of activeRates) {
        if (
          activeRate instanceof Rate &&
          this.incomingRateSubscriptions.hasOwnProperty(activeRate.key)
        ) {
          let subscrKey: string = activeRate.key;
          let ccyPairModel: CubiqTS.CurrencyPairImpl =
            this.incomingRateSubscriptions[subscrKey].ccyPairModel;
          let rateDataModel = this.utility.transformRateToOERateDataModel(
            subscrKey,
            activeRate,
            ccyPairModel,
          );
          if (rateDataModel instanceof OERateDataModel) {
            this.incomingRateSubscriptions[subscrKey].value = rateDataModel;

            if (
              this.outgoingRateSubscriptions.hasOwnProperty(subscrKey) &&
              this.outgoingRateSubscriptions[subscrKey].state === OENRateSubscriptionState.ACTIVE
            ) {
              // AUG add checking for state
              let refIdsList = this.outgoingRateSubscriptions[subscrKey].refIds;
              if (refIdsList instanceof Array && refIdsList.length > 0) {
                this.outputExtendedLogging(
                  `INFO: oeRSM: Sending rate ${
                    rateDataModel.rateValue.toFixed(4) +
                    " / i: " +
                    rateDataModel.invertedRateValue.toFixed(4)
                  } for ${subscrKey} to ${refIdsList.toString()}`,
                );
                this.outgoingRateSubscriptions[subscrKey].subject.next(
                  new OERateTransportModel(refIdsList, rateDataModel),
                );
              } else {
                logger.out.warn(
                  `WARNING: oeRSM.processIncomingRates(): no outgoing subscriptions for ${subscrKey} => `,
                  this.outgoingRateSubscriptions[subscrKey].refIds,
                );
              }
            } else {
              logger.out.warn(
                `INFO: oeRSM: Sending rate skipped as state != OENRateSubscriptionState.ACTIVE`,
              );
            }
          } else {
            logger.out.warn(
              `WARNING: oeRSM.processIncomingRates(): Error processing incoming rate, nothing forwarded to subscribers`,
            );
          }
        } else {
          logger.out.error(
            `ERROR: oeRSM.processIncomingRates(): activeRate is not a Rate or not registered in incomingRateSubscriptions`,
          );
        }
      }
    }
  }

  private subscribeIncomingRate(ccyPair: string, rateSubscription: RateSubscription): void {
    this.rateSubscriptionService.hasSubscription(rateSubscription).then((outcome) => {
      if (outcome) {
        let activeRate = this.rateCache.getRate(ccyPair);
        if (activeRate instanceof Rate && activeRate.tradable) {
          this.processIncomingRates([activeRate]);
        }
      } else {
        this.rateSubscriptionService.subscribe(rateSubscription).then((result) => {
          if (result != null && result.rateData != null) {
            // instanceof CubiqTS.MarketDataEventImpl
            let rate: Array<Rate> = this.rateTransformer.transformEvent(result);
            if (rate[0] instanceof Rate) {
              this.processIncomingRates([rate[0]]);
            }
          } /* else{ // fallback
						let activeRate = this.rateCache.getRate(ccyPair);
						if(activeRate instanceof Rate){
							this.processIncomingRates([activeRate]);
						}
					} */
        });
      }
    });
  }

  private unsubscribeIncomingRate(rateSubscription: RateSubscription): void {
    if (rateSubscription instanceof RateSubscription) {
      this.rateSubscriptionService.hasSubscription(rateSubscription).then((outcome) => {
        if (outcome) {
          this.rateSubscriptionService
            .unsubscribeWithCheck(rateSubscription)
            .then((result: any) => {
              if (typeof result === "boolean" && result === false) {
                this.outputExtendedLogging(
                  `INFO: oeRSM-WORKER: Subscription [${rateSubscription.key}]  WORKER-UNSUBSCRIBE => CANCEL (Used in Blotter)!`,
                );
              } else {
                this.outputExtendedLogging(
                  `INFO: oeRSM-WORKER: Subscription [${rateSubscription.key}]  WORKER-UNSUBSCRIBE => DONE!`,
                );
              }
            });
        }
      });
    }
  }

  /**********************************************************************/
  /* PUBLIC API */

  // this.lookup.getAccountSubject().subscribe(this.onAccountUpdateEvent.bind(this));
  public subscribe(rootRefId: string, entryType: OENEntryType, ccyPair: string): Subject<any> {
    if (
      typeof rootRefId === "string" &&
      typeof entryType === "number" &&
      typeof ccyPair === "string" &&
      ccyPair.length === 6
    ) {
      let subscrKey: string = this.getSubscrKey(ccyPair);
      this.outputExtendedLogging(
        `INFO: oeRSM: SUBSCRIBE ${rootRefId} for ${ccyPair} [${subscrKey}]`,
      );
      if (typeof subscrKey === "string") {
        try {
          // get ccyPair model
          let currencyPairLookup: CcyPairLookupService = this.currencyPairService.getBase();
          let ccyPairModel = currencyPairLookup.getItem(ccyPair);
          let ccyPairSubscribed = ccyPair;
          if (!(ccyPairModel instanceof CubiqTS.CurrencyPairImpl)) {
            // check if inverted
            ccyPairModel = currencyPairLookup.getCcyPair(
              ccyPair.substr(3, 3),
              ccyPair.substr(0, 3),
            );
            (ccyPairSubscribed = ccyPair.substr(3, 3)), ccyPair.substr(0, 3);
          }

          if (ccyPairModel instanceof CubiqTS.CurrencyPairImpl) {
            // OUTGOING
            if (this.outgoingRateSubscriptions.hasOwnProperty(subscrKey)) {
              if (!this.outgoingRateSubscriptions[subscrKey].refIds.includes(rootRefId)) {
                this.outgoingRateSubscriptions[subscrKey].refIds.push(rootRefId);
              } else {
                logger.out.warn(
                  `WARNING: oeRSM.subscribe(): refId [${rootRefId}] already in outgoing subscription list`,
                );
              }
              this.outgoingRateSubscriptions[subscrKey].state = OENRateSubscriptionState.ACTIVE;
            } else {
              let newOutgoingSubscription = new OEOutgoingRateSubscriptionModel(subscrKey);
              newOutgoingSubscription.state = OENRateSubscriptionState.PENDING;
              newOutgoingSubscription.refIds = [];
              newOutgoingSubscription.refIds.push(rootRefId);
              newOutgoingSubscription.ccyPair = ccyPair;
              newOutgoingSubscription.invertedCcyPair = ccyPair.substr(3, 3) + ccyPair.substr(0, 3);
              this.outgoingRateSubscriptions = {
                ...this.outgoingRateSubscriptions,
                [subscrKey]: newOutgoingSubscription,
              };
            }
            this.outgoingRateSubscriptions[subscrKey].state = OENRateSubscriptionState.ACTIVE;
            this.outputExtendedLogging(
              `INFO: oeRSM: SUBSCRIBE ${rootRefId}  => DONE | Outgoing MAP: ${subscrKey}: ${
                this.outgoingRateSubscriptions[subscrKey].refIds.length > 0
                  ? this.outgoingRateSubscriptions[subscrKey].refIds
                  : "EMPTY"
              }`,
            );

            // INCOMING
            if (!this.incomingRateSubscriptions.hasOwnProperty(subscrKey)) {
              let rateSubscription = this.getRateSubscription(ccyPair);
              let newIncomingSubscription = new OEIncomingRateSubscriptionModel(
                subscrKey,
                rateSubscription,
              );
              newIncomingSubscription.ccyPairModel = ccyPairModel;
              newIncomingSubscription.subscribedCcyPair = ccyPairSubscribed;
              this.incomingRateSubscriptions = {
                ...this.incomingRateSubscriptions,
                [subscrKey]: newIncomingSubscription,
              };
              this.subscribeIncomingRate(ccyPairSubscribed, rateSubscription);
            }

            // send empty model to the new subscriber initially
            let initialRate = this.createInitialRateDataModel(ccyPair, ccyPairModel, subscrKey);

            if (this.outgoingRateSubscriptions[subscrKey].subject != null) {
              this.outgoingRateSubscriptions[subscrKey].subject.complete();
            }

            this.outgoingRateSubscriptions[subscrKey].subject =
              new ReplaySubject<OERateTransportModel>(1);

            if (initialRate instanceof OERateDataModel) {
              this.outputExtendedLogging(`INFO: oeRSM: Sending EMPTY rate to ${rootRefId}`);
              this.outgoingRateSubscriptions[subscrKey].subject.next(
                new OERateTransportModel([rootRefId], initialRate),
              );
            }

            return this.outgoingRateSubscriptions[subscrKey].subject;
          } else {
            logger.out.error(
              `ERROR: oeRSM.subscribe(): CurrencyPairImpl not found for ccyPair ${ccyPair}`,
            );
            return null;
          }
        } catch (ex) {
          logger.out.error(`ERROR: oeRSM.subscribe(): Exception: ${ex}`);
          return null;
        }
      } else {
        logger.out.error(`ERROR: oeRSM.subscribe(): Unknown rate subscription key`);
        return null;
      }
    } else {
      logger.out.error(`ERROR: oeRSM.subscribe(): Invalid input parameters`);
      return null;
    }
  }

  public unsubscribe(rootRefId: string, entryType: OENEntryType, ccyPair: string): void {
    if (
      typeof rootRefId === "string" &&
      typeof entryType === "number" &&
      typeof ccyPair === "string" &&
      ccyPair.length === 6
    ) {
      let subscrKey: string = this.getSubscrKey(ccyPair);
      this.outputExtendedLogging(
        `INFO: oeRSM: UN-SUBSCRIBE ${rootRefId} for ${ccyPair} [${subscrKey}]`,
      );
      if (typeof subscrKey === "string") {
        if (
          this.outgoingRateSubscriptions.hasOwnProperty(subscrKey) &&
          this.incomingRateSubscriptions.hasOwnProperty(subscrKey)
        ) {
          if (this.outgoingRateSubscriptions[subscrKey].refIds.includes(rootRefId)) {
            let index = this.outgoingRateSubscriptions[subscrKey].refIds.indexOf(rootRefId); //.filter(item => item !== rootRefId); - not suitable
            if (index >= 0) {
              this.outgoingRateSubscriptions[subscrKey].refIds.splice(index, 1);
            }
            // unsubscribe incoming if no outgoing
            if (this.outgoingRateSubscriptions[subscrKey].refIds.length < 1) {
              // if delete is not already scheduled, cancel it and schedule a new one
              if (this.deleteTimeoutScheduled != null) {
                clearTimeout(this.deleteTimeoutScheduled);
                this.deleteTimeoutScheduled == null;
                this.outputExtendedLogging(
                  `INFO: oeRSM: Previously scheduled incoming subscription canceled, new one scheduled`,
                );
              }

              this.outgoingRateSubscriptions[subscrKey].state = OENRateSubscriptionState.DELETE;
              this.deleteSubscription(subscrKey);

              // wipe any rates for this pair from the cache -
              // so they can't be found during a subscribe later -
              // as they may be well stale by then
              // this.rateCache.clearRate(ccyPair);
            }
            this.outputExtendedLogging(
              `INFO: oeRSM: UN-SUBSCRIBE ${rootRefId}  => DONE | Outgoing MAP: ${subscrKey}: ${
                this.outgoingRateSubscriptions[subscrKey].refIds.length > 0
                  ? this.outgoingRateSubscriptions[subscrKey].refIds
                  : "EMPTY"
              }`,
            );
          } else {
            logger.out.error(
              `ERROR: oeRSM.unsubscribe(): RefId not found in outgoing subscription`,
            );
          }
        } else {
          logger.out.error(
            `ERROR: oeRSM.unsubscribe(): Outgoing or Incoming rate subscription object not found`,
          );
        }
      } else {
        logger.out.error(`ERROR: oeRSM.unsubscribe(): Unknown rate subscription key`);
      }
    }
  }

  public takeRateSubscription(
    rootRefId: string,
    oldRefId: string,
    entryType: OENEntryType,
    ccyPair: string,
  ): void {
    if (
      typeof rootRefId === "string" &&
      typeof entryType === "number" &&
      typeof ccyPair === "string" &&
      ccyPair.length === 6
    ) {
      let subscrKey: string = this.getSubscrKey(ccyPair);
      this.outputExtendedLogging(
        `INFO: oeRSM: TAKE SUBSCRIPTION ${rootRefId} for ${ccyPair} [${subscrKey}]`,
      );
      if (typeof subscrKey === "string") {
        if (
          this.outgoingRateSubscriptions.hasOwnProperty(subscrKey) &&
          this.incomingRateSubscriptions.hasOwnProperty(subscrKey)
        ) {
          if (this.outgoingRateSubscriptions[subscrKey].refIds.includes(oldRefId)) {
            let index = this.outgoingRateSubscriptions[subscrKey].refIds.indexOf(oldRefId);
            if (index >= 0) {
              this.outgoingRateSubscriptions[subscrKey].refIds.splice(index, 1);
            }
            this.outgoingRateSubscriptions[subscrKey].refIds.push(rootRefId);
            this.outputExtendedLogging(
              `INFO: oeRSM:  TAKE SUBSCRIPTION  ${rootRefId}  => DONE | Outgoing MAP: ${subscrKey}: ${
                this.outgoingRateSubscriptions[subscrKey].refIds.length > 0
                  ? this.outgoingRateSubscriptions[subscrKey].refIds
                  : "EMPTY"
              }`,
            );
          } else {
            logger.out.error(
              `ERROR: oeRSM.takeRateSubscription(): RefId not found in outgoing subscription`,
            );
          }
        } else {
          logger.out.error(
            `ERROR: oeRSM.takeRateSubscription(): Outgoing or Incoming rate subscription object not found`,
          );
        }
      } else {
        logger.out.error(`ERROR: oeRSM.takeRateSubscription(): Unknown rate subscription key`);
      }
    }
  }

  /**********************************************************************/
  /* HELPERS */
  private getSubscrKey(ccyPair: string, entryType: OENEntryType = OENEntryType.SPOT): string {
    let rateSubscription = this.getRateSubscription(ccyPair, entryType);
    return rateSubscription instanceof RateSubscription ? rateSubscription.key : null;
  }

  private getRateSubscription(
    ccyPair: string,
    entryType: OENEntryType = OENEntryType.SPOT,
  ): RateSubscription {
    if (typeof ccyPair === "string" && ccyPair.length === 6) {
      let ccyPairToSubscribe: string = null;
      // find ccyPair to subscribe, inverts can not be subscribed to
      let currencyPairLookup: CcyPairLookupService = this.currencyPairService.getBase();
      let ccyPairModel = currencyPairLookup.getItem(ccyPair);
      if (!(ccyPairModel instanceof CubiqTS.CurrencyPairImpl)) {
        // check if inverted
        ccyPairModel = currencyPairLookup.getCcyPair(ccyPair.substr(3, 3), ccyPair.substr(0, 3));
        if (ccyPairModel instanceof CubiqTS.CurrencyPairImpl) {
          ccyPairToSubscribe = ccyPair.substr(3, 3) + ccyPair.substr(0, 3);
        } else {
          //CurrencyPairImpl not found
          ccyPairToSubscribe = null;
        }
      } else {
        ccyPairToSubscribe = ccyPair;
      }
      // CRATE SUBSCRIPTION
      if (typeof ccyPairToSubscribe === "string") {
        let rateSubscription = null;
        switch (entryType) {
          case OENEntryType.SPOT:
          case OENEntryType.NDF:
          case OENEntryType.ALGO:
          case OENEntryType.NDF_ALGO:
          case OENEntryType.BENCHMARK:
            rateSubscription = new RateSubscription(
              ccyPairToSubscribe,
              CURVE_SPT,
              CubiqTS.Tenor.SPOT,
              null,
            );
            break;
        }
        return rateSubscription instanceof RateSubscription ? rateSubscription : null;
      } else {
        return null;
      }
    } else {
      return null;
    }
  }

  private createEmptyRateDataModel(subscrKey: string, ccyPair: string): OERateDataModel {
    if (typeof subscrKey === "string" && typeof ccyPair === "string" && ccyPair.length === 6) {
      try {
        /* DATA MODEL */
        let rateData = new OERateDataModel(subscrKey);
        // value
        rateData.rateValue = new CubiqTS.BigNumber(0);
        rateData.invertedRateValue = new CubiqTS.BigNumber(0);
        // display
        rateData.displayModel = OERateDisplayModel.empty();
        rateData.invertedDisplayModel = OERateDisplayModel.empty();
        // ccyPair
        rateData.ccyPair = ccyPair;
        rateData.invertedCcyPair = ccyPair.substr(3, 3) + ccyPair.substr(0, 3);

        return rateData;
      } catch (ex) {
        logger.out.error(`ERROR: oeRSM.createEmptyRateDataModel(): Exception: ${ex}`);
        return null;
      }
    } else {
      logger.out.error(`ERROR: oeRSM.createEmptyRateDataModel(): Invalid input parameter`);
      return null;
    }
  }

  public createInitialRateDataModel(
    ccyPair: string,
    ccyPairModel,
    subscrKey: string = this.getSubscrKey(ccyPair),
  ) {
    let initialRate = this.createEmptyRateDataModel(subscrKey, ccyPair);
    // load the cached Rate
    // xform it into something we can eat and put it in the model
    // next tick, if any, will overwrite it
    let pairId: string = ccyPairModel != null ? ccyPairModel.base + ccyPairModel.nonBase : ccyPair;
    let cachedSpotRate: Rate = this.rateCache.getRate(pairId);
    if (cachedSpotRate) {
      let xformedCachedRate: OERateDataModel = this.utility.transformRateToOERateDataModel(
        ccyPair,
        cachedSpotRate,
        ccyPairModel,
      );
      if (xformedCachedRate) {
        initialRate = xformedCachedRate;
      }
    }
    return initialRate;
  }

  private deleteSubscription(subscrKey: string): void {
    if (typeof subscrKey === "string" && this.outgoingRateSubscriptions.hasOwnProperty(subscrKey)) {
      this.deleteTimeoutScheduled = setTimeout(() => {
        if (
          this.outgoingRateSubscriptions.hasOwnProperty(subscrKey) &&
          this.incomingRateSubscriptions.hasOwnProperty(subscrKey)
        ) {
          if (this.outgoingRateSubscriptions[subscrKey].state === OENRateSubscriptionState.DELETE) {
            this.unsubscribeIncomingRate(
              this.incomingRateSubscriptions[subscrKey].rateSubscription,
            );

            delete this.outgoingRateSubscriptions[subscrKey];
            delete this.incomingRateSubscriptions[subscrKey];

            this.outputExtendedLogging(
              `INFO: oeRSM.deleteSubscription(): [${subscrKey}] SUBSCRIPTION DELETE => DONE`,
            );
          } else {
            this.outputExtendedLogging(
              `INFO: oeRSM.deleteSubscription(): [${subscrKey}] SUBSCRIPTION DELETE => CANCELED`,
            );
          }
          this.deleteTimeoutScheduled == null;
        } else {
          logger.out.error(
            `ERROR: oeRSM.deleteSubscription(): Outgoing or Incoming rate subscription model not found`,
          );
        }
      }, 10000);
    } else {
      logger.out.error(
        `ERROR: oeRSM.deleteSubscription(): Operation CANCELED -> invalid key or missing objects`,
      );
    }
  }

  /**********************************************************************/
  /* HELPERS - Debug Log - Extended logging  */

  private outputExtendedLogging(value: string, dump: any = null): void {
    this.oeLogger.outputRateLogging(value, dump);
    /* 	if(dump != null){
			logger.out.info('DEV-CCY: Service -> ' + value, dump);
		}else{
			logger.out.info('DEV-CCY: Service -> ' + value);
		} */
  }
}
